// src/pages/admin/tabs/awards/AwardEditPage.jsx

import React from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import PropertyImagesPicker from '../../components/PropertyImagesPicker';
import MediaPickerModal from '../../components/MediaPickerModal';

const Wrap = styled.div`display:grid; gap:20px;`;
const Card = styled.div`
  border:1px solid var(--bdr); background:var(--card); border-radius:14px; padding:16px; box-shadow:var(--shadow);
`;
const Grid2 = styled.div`
  display:grid; grid-template-columns:1fr 1fr; gap:12px;
  @media (max-width: 980px){ grid-template-columns:1fr; }
`;
const Field = styled.label`
  display:grid; gap:6px;
  input, textarea, select { width:100%; }
  textarea{ min-height: 110px; }
`;
const Actions = styled.div`display:flex; gap:10px; flex-wrap:wrap; align-items:center;`;
const Btn = styled.button`
  padding:10px 14px; border-radius:12px; border:1px solid var(--bdr);
  background:var(--card); font-weight:700; cursor:pointer;
  &[data-primary="true"]{ background:var(--primary); color:var(--primaryFg); border-color:transparent; }
  &[disabled]{ opacity:.6; cursor:default; }
`;
const Pills = styled.div`display:flex; gap:8px; flex-wrap:wrap; align-items:center;`;
const Pill = styled.span`
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 10px; border:1px solid var(--bdr); border-radius:999px; font-size:13px; opacity:.85;
`;

function ToggleYesNo({ value, onChange }){
  const v = !!value;
  return (
    <div style={{display:'inline-flex', border:'1px solid var(--bdr)', borderRadius:10, overflow:'hidden'}}>
      <button type="button" onClick={()=>onChange(true)}  style={{padding:'8px 12px', fontWeight:700, border:'none', background: v ? 'var(--primary)' : 'var(--card)', color: v ? 'var(--primaryFg)' : 'inherit'}}>Да</button>
      <button type="button" onClick={()=>onChange(false)} style={{padding:'8px 12px', fontWeight:700, border:'none', background: !v ? 'var(--primary)' : 'var(--card)', color: !v ? 'var(--primaryFg)' : 'inherit'}}>Нет</button>
    </div>
  );
}

export default function AwardEditPage(){
  const { id } = useParams(); // undefined => create NEW
  const isEdit = !!id;
  const nav = useNavigate();

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // поля
  const [title_ru, setTitleRu] = React.useState('');
  const [title_en, setTitleEn] = React.useState('');
  const [desc_ru, setDescRu]   = React.useState('');
  const [desc_en, setDescEn]   = React.useState('');
  const [organization, setOrg] = React.useState('');
  const [category, setCategory]= React.useState('');
  const [year, setYear]        = React.useState('');
  const [sort_order, setSort]  = React.useState(0);
  const [is_active, setActive] = React.useState(true);
  const [is_featured, setFeatured] = React.useState(false);

  // картинка: используем PropertyImagesPicker как single
  const [images, setImages] = React.useState([]); // [{id,url,thumbnail_url,is_main}]
  const mainImage = React.useMemo(()=>{
    if (!images?.length) return null;
    return images.find(x=>x.is_main) || images[0];
  }, [images]);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const onPickImage = (list)=>{
    const file = list?.[0]; if (!file) return;
    setImages([{ id: file.id, url: file.url, thumbnail_url: file.thumbnail_url || file.url, is_main: true }]);
    setPickerOpen(false);
  };

  // загрузка
  React.useEffect(()=>{
    let stop = false;
    (async ()=>{
      if (!isEdit) return;
      try{
        const j = await AdminAPI.getAward(id);
        const it = j.award || j.item || j;
        if (stop || !it) return;

        setTitleRu(it.title_ru || it.title || '');
        setTitleEn(it.title_en || it.title || '');
        setDescRu(it.description_ru || '');
        setDescEn(it.description_en || '');
        setOrg(it.organization || '');
        setCategory(it.category || '');
        setYear(it.year ?? '');
        setSort(it.sort_order ?? 0);
        setActive(!!it.is_active);
        setFeatured(!!it.is_featured);

        if (it.featured_media?.id && (it.featured_media?.thumbnail_url || it.featured_media?.url)){
          setImages([{
            id: it.featured_media.id,
            url: it.featured_media.url,
            thumbnail_url: it.featured_media.thumbnail_url || it.featured_media.url,
            is_main: true
          }]);
        } else if (it.image){
          setImages([{ id: undefined, url: it.image, thumbnail_url: it.image_thumb || it.image, is_main: true }]);
        } else {
          setImages([]);
        }
      }catch(e){
        console.error(e);
        setErr(e.message || 'Не удалось загрузить запись');
      }
    })();
    return ()=>{ stop = true; };
  }, [isEdit, id]);

  // сохранение
  const save = async ()=>{
    setErr(''); setOk(''); setSaving(true);
    try{
      if (!title_ru && !title_en) throw new Error('Укажите заголовок RU или EN');

      const payload = {
        title_ru: title_ru || undefined,
        title_en: title_en || undefined,
        description_ru: desc_ru || undefined,
        description_en: desc_en || undefined,
        organization: organization || undefined,
        category: category || undefined,
        year: year === '' ? undefined : Number(year),
        sort_order: Number(sort_order) || 0,
        is_active: !!is_active,
        is_featured: !!is_featured,

        // если выбран из медиатеки — отправим id; если просто URL — отправим image
        featured_media_id: mainImage?.id || undefined,
        image: (!mainImage?.id && mainImage?.url) ? mainImage.url : undefined,
      };

      if (isEdit){
        await AdminAPI.updateAward(id, payload);
        setOk('Сохранено');
      }else{
        const res = await AdminAPI.createAward(payload);
        const newId = res?.award?.id || res?.id;
        if (!newId) throw new Error('Создано, но id не получен');
        setOk('Создано');
        nav(`/admin/awards/${newId}`);
        return;
      }
    }catch(e){
      setErr(e?.payload?.details ? JSON.stringify(e.payload.details) : (e.message || 'Ошибка сохранения'));
    }finally{
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <Card>
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <h3 style={{margin:0}}>{isEdit ? 'Редактировать награду' : 'Новая награда'}</h3>
          <div style={{flex:1}} />
          <Link to="/admin/awards" className="btn">← К списку</Link>
        </div>
      </Card>

      <Card>
        {err && <div style={{color:'#e66', marginBottom:8}}>{err}</div>}
        {ok &&  <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <Grid2>
          <Field>Заголовок RU
            <input value={title_ru} onChange={e=>setTitleRu(e.target.value)} placeholder="Напр.: Best Developer Award" />
          </Field>
          <Field>Заголовок EN
            <input value={title_en} onChange={e=>setTitleEn(e.target.value)} placeholder="E.g. Best Developer Award" />
          </Field>
        </Grid2>

        <Grid2>
          <Field>Организация
            <input value={organization} onChange={e=>setOrg(e.target.value)} placeholder="Кем выдано" />
          </Field>
          <Field>Категория
            <input value={category} onChange={e=>setCategory(e.target.value)} placeholder="award | certificate | recognition" list="award-cats" />
            <datalist id="award-cats">
              <option value="award" />
              <option value="certificate" />
              <option value="recognition" />
            </datalist>
          </Field>
        </Grid2>

        <Grid2>
          <Field>Год
            <input type="number" value={year} onChange={e=>setYear(e.target.value)} placeholder="2024" />
          </Field>
          <Field>Сортировка
            <input type="number" value={sort_order} onChange={e=>setSort(e.target.value)} />
          </Field>
        </Grid2>

        <Grid2>
          <Field>Описание RU
            <textarea value={desc_ru} onChange={e=>setDescRu(e.target.value)} placeholder="Короткое описание (RU)" />
          </Field>
          <Field>Описание EN
            <textarea value={desc_en} onChange={e=>setDescEn(e.target.value)} placeholder="Short description (EN)" />
          </Field>
        </Grid2>

        <Grid2>
          <Field>Активна
            <ToggleYesNo value={is_active} onChange={setActive} />
          </Field>
          <Field>Избранная
            <ToggleYesNo value={is_featured} onChange={setFeatured} />
          </Field>
        </Grid2>
      </Card>

      {/* Изображение через медиатеку (одно) */}
      <Card>
        <h3 style={{marginTop:0}}>Изображение</h3>
        <div style={{opacity:.75, marginBottom:8, fontSize:14}}>
          Выберите одно изображение (то, что помечено «Главная», будет использовано).
        </div>
        <PropertyImagesPicker
          value={images}
          onChange={setImages}
          autoDeleteUploads={false}
          single // (если в компоненте предусмотрен такой проп; если нет — просто оставьте как есть и выбирайте одно вручную)
        />
        <div style={{marginTop:8}}>
          <button type="button" className="btn" onClick={()=>setPickerOpen(true)}>Выбрать из медиатеки</button>
        </div>
      </Card>

      <Card>
        <Actions>
          <Btn data-primary="true" onClick={save} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить'}</Btn>
          <Link to="/admin/awards" className="btn">Отмена</Link>
          <div style={{flex:1}} />
          {isEdit && <Pills><Pill>id: <b>{id}</b></Pill></Pills>}
        </Actions>
      </Card>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={onPickImage} multi={false} />
    </Wrap>
  );
}
